// STRIKZA — SLIP REPAIR · icon set
//
// Ported verbatim from the handoff prototype's SrIcon set. The
// production app's shared `Icon` component covers most chrome, but
// the Repair surface ships a few additional glyphs (wrench, bolt,
// share, print, copy, pinpoint, extract) that the chrome set does
// not. Keeping them local avoids polluting the global icon module
// for a single page.
import type { ReactElement, ReactNode } from "react";

export type SrIconName =
  | "upload"
  | "file"
  | "wrench"
  | "arrow"
  | "arrowR"
  | "check"
  | "x"
  | "undo"
  | "refresh"
  | "alert"
  | "info"
  | "shield"
  | "lock"
  | "bolt"
  | "download"
  | "share"
  | "sparkle"
  | "extract"
  | "pinpoint"
  | "clock"
  | "print"
  | "copy"
  | "cap";

const PATHS: Record<SrIconName, ReactNode> = {
  upload: (
    <>
      <path d="M12 17V5" />
      <path d="m6 11 6-6 6 6" />
      <path d="M5 19h14" />
    </>
  ),
  file: (
    <>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
    </>
  ),
  wrench: (
    <>
      <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.5-2.5Z" />
    </>
  ),
  arrow: (
    <>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </>
  ),
  arrowR: (
    <>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </>
  ),
  check: <path d="M5 12l4 4 10-10" />,
  x: (
    <>
      <path d="M6 6l12 12M18 6L6 18" />
    </>
  ),
  undo: (
    <>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 3v5h-5" />
    </>
  ),
  refresh: (
    <>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 21v-5h5" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4 2 20h20Z" />
      <path d="M12 10v5M12 18v.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v5h1" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3 4 6v7c0 4.5 3.5 7.5 8 8 4.5-.5 8-3.5 8-8V6Z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
  bolt: <path d="m13 2-9 12h7l-1 8 9-12h-7l1-8Z" />,
  download: (
    <>
      <path d="M12 4v12" />
      <path d="m6 10 6 6 6-6" />
      <path d="M5 20h14" />
    </>
  ),
  share: (
    <>
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8 10.5 16 7M8 13.5 16 17" />
    </>
  ),
  sparkle: (
    <>
      <path d="M12 3v18M3 12h18M5 5l14 14M19 5 5 19" />
    </>
  ),
  extract: (
    <>
      <path d="M6 3h9l3 3v15a1 1 0 0 1-1 1H6Z" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </>
  ),
  pinpoint: (
    <>
      <circle cx="12" cy="11" r="3" />
      <path d="M12 2a8 8 0 0 0-8 8c0 5 8 12 8 12s8-7 8-12a8 8 0 0 0-8-8Z" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  print: (
    <>
      <path d="M6 9V3h12v6" />
      <rect x="3" y="9" width="18" height="9" rx="1" />
      <path d="M6 14h12v6H6z" />
    </>
  ),
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </>
  ),
  cap: (
    <>
      <path d="M3 8h18l-2 13H5Z" />
      <path d="M9 8V4a3 3 0 0 1 6 0v4" />
    </>
  ),
};

interface SrIconProps {
  name: SrIconName;
  size?: number;
}

export function SrIcon({ name, size = 14 }: SrIconProps): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {PATHS[name] ?? null}
    </svg>
  );
}

export const fmtMoney = (n: number): string =>
  n.toLocaleString("en-GH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtOdds = (n: number): string => n.toFixed(2);

export function bandFromScore(s: number): "strong" | "good" | "fair" | "needs-work" {
  if (s >= 80) return "strong";
  if (s >= 60) return "good";
  if (s >= 40) return "fair";
  return "needs-work";
}

export function bandLabel(b: "strong" | "good" | "fair" | "needs-work"): string {
  return ({ strong: "STRONG", good: "GOOD", fair: "FAIR", "needs-work": "NEEDS WORK" } as const)[b];
}

export function bandCopy(b: "strong" | "good" | "fair" | "needs-work"): string {
  return ({
    strong: "Your slip is well-constructed. Only minor optimisations available.",
    good: "Your slip has solid foundations. A few legs can be improved.",
    fair: "Your slip has opportunities to improve. Review the flagged legs carefully.",
    "needs-work":
      "Your slip has significant void risk and weak edge. Full repair recommended before staking.",
  } as const)[b];
}
